import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { TicketListResponse, TicketFilters } from '../types';

/**
 * Ticket API Service
 */
export const ticketApi = {
    /**
     * Get tickets for admin with filters
     */
    getAdminTickets: async (schoolSlug: string, filters: TicketFilters): Promise<TicketListResponse> => {
        try {
            const response = await apiClient.get<TicketListResponse>(
                API_ENDPOINTS.TICKETS.ADMIN_LIST(schoolSlug),
                { params: filters }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch tickets'
            };
        }
    },

    /**
     * Get ticket details
     */
    getTicketDetail: async (schoolSlug: string, ticketId: string, employeeId?: string): Promise<any> => {
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.TICKETS.DETAIL(schoolSlug, ticketId),
                { params: { employee_id: employeeId } }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch ticket details'
            };
        }
    },

    /**
     * Update ticket general details
     */
    updateTicket: async (schoolSlug: string, ticketId: string, data: Partial<any>): Promise<any> => {
        try {
            const response = await apiClient.patch(API_ENDPOINTS.TICKETS.DETAIL(schoolSlug, ticketId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update ticket'
            };
        }
    },

    /**
     * Update specific ticket status with comments
     */
    updateTicketStatus: async (schoolSlug: string, ticketId: string, data: { status: string; comment: string }): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.TICKETS.UPDATE_STATUS(schoolSlug, ticketId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update ticket status'
            };
        }
    },

    /**
     * Get ticket audit history
     */
    getTicketHistory: async (schoolSlug: string, ticketId: string): Promise<any> => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.TICKETS.HISTORY(schoolSlug, ticketId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch ticket history'
            };
        }
    },

    /**
     * Change ticket workflow (move to a different workflow/department)
     */
    changeTicketWorkflow: async (schoolSlug: string, ticketId: string, data: { target_workflow_id: string; comment: string }): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.TICKETS.CHANGE_WORKFLOW(schoolSlug, ticketId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to change ticket workflow'
            };
        }
    }
};
